// useShowerState.js
// Showers division (08 00 02) - UI state and simple handlers

import { useState, useCallback } from 'react';

// Default marker color for showers
const DEFAULT_MARKER_COLOR = '#10B981'; // Emerald green

export const useShowerState = () => {
  // ============================================
  // OPERATION LOADING STATE
  // ============================================
  const [showerOperationLoading, setShowerOperationLoading] = useState(false);
  
  // ============================================
  // SELECTION STATES
  // ============================================
  const [selectedShowerTypeIds, setSelectedShowerTypeIds] = useState([]);
  
  // ============================================
  // SHOWER TYPE DIALOG STATE
  // ============================================
  const [showShowerTypeDialog, setShowShowerTypeDialog] = useState(false);
  const [editingShowerType, setEditingShowerType] = useState(null);
  const [newShowerType, setNewShowerType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    operationType: '',
    finishWarranty: '',
    glassWarranty: '',
    hardwareWarranty: '',
    installationWarranty: '',
    markerColor: DEFAULT_MARKER_COLOR
  });
  
  // ============================================
  // SHOWER TYPE PICTURE STATE
  // ============================================
  const [pendingShowerTypePicture, setPendingShowerTypePicture] = useState(null);
  const [showerTypePictureUploading, setShowerTypePictureUploading] = useState(null);
  const [showerTypePictureUrl, setShowerTypePictureUrl] = useState(null);
  
  // ============================================
  // DELETE CONFIRMATION STATE
  // ============================================
  const [showDeleteShowerTypeModal, setShowDeleteShowerTypeModal] = useState(false);
  const [deleteShowerTypeId, setDeleteShowerTypeId] = useState(null);
  
  // ============================================
  // SHOWER GLASS TYPE STATE
  // ============================================
  const [showShowerGlassTypeDialog, setShowShowerGlassTypeDialog] = useState(false);
  const [editingShowerGlassType, setEditingShowerGlassType] = useState(null);
  const [selectedShowerGlassTypeIds, setSelectedShowerGlassTypeIds] = useState([]);
  const [expandedShowerGlassTypeIds, setExpandedShowerGlassTypeIds] = useState([]);
  const [newShowerGlassType, setNewShowerGlassType] = useState({
    name: '', manufacturer: '', glassBuild: '', glassThickness: '', features: [], color: '', warrantyYears: ''
  });
  const [showDeleteShowerGlassTypeModal, setShowDeleteShowerGlassTypeModal] = useState(false);
  const [deleteShowerGlassTypeId, setDeleteShowerGlassTypeId] = useState(null);

  // ============================================
  // SHOWER HARDWARE SET STATE
  // ============================================
  const [showShowerHardwareSetDialog, setShowShowerHardwareSetDialog] = useState(false);
  const [editingShowerHardwareSet, setEditingShowerHardwareSet] = useState(null);
  const [selectedShowerHardwareSetIds, setSelectedShowerHardwareSetIds] = useState([]);
  const [newShowerHardwareSet, setNewShowerHardwareSet] = useState({ name: '', items: [] });
  const [newShowerHardwareItem, setNewShowerHardwareItem] = useState({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
  const [showerHardwareSetStep, setShowerHardwareSetStep] = useState(1);
  const [editingShowerHardwareItemIndex, setEditingShowerHardwareItemIndex] = useState(null);
  const [showDeleteShowerHardwareSetModal, setShowDeleteShowerHardwareSetModal] = useState(false);
  const [deleteShowerHardwareSetId, setDeleteShowerHardwareSetId] = useState(null);

  // ============================================
  // SHOWER SCHEDULE STATE
  // ============================================
  const [showShowerScheduleDialog, setShowShowerScheduleDialog] = useState(false);
  const [editingShowerSchedule, setEditingShowerSchedule] = useState(null);
  const [selectedShowerScheduleIds, setSelectedShowerScheduleIds] = useState([]);
  const [newShowerSchedule, setNewShowerSchedule] = useState({ label: '', showerTypeId: '', hardwareSetId: '' });
  const [showShowerDetailDialog, setShowShowerDetailDialog] = useState(false);
  const [selectedShowerDetail, setSelectedShowerDetail] = useState(null);

  // Shower Schedule Filter state
  const [showerScheduleSearch, setShowerScheduleSearch] = useState('');
  const [showerScheduleFilters, setShowerScheduleFilters] = useState({ type: '', location: '' });
  const [showerScheduleSortBy, setShowerScheduleSortBy] = useState('createdAt_desc');

  // ============================================
  // SHOWER FLOOR PLAN STATE
  // ============================================
  const [selectedShowerElevation, setSelectedShowerElevation] = useState(null);
  const [selectedShowerElevationIds, setSelectedShowerElevationIds] = useState([]);
  const [showerBulkMarkerMode, setShowerBulkMarkerMode] = useState(false);
  const [showerBulkMarkerSchedule, setShowerBulkMarkerSchedule] = useState('');
  const [pendingShowerBulkMarkers, setPendingShowerBulkMarkers] = useState([]);
  const [savingShowerBulkMarkers, setSavingShowerBulkMarkers] = useState(false);
  
  // ============================================
  // RESET FUNCTIONS
  // ============================================
  const resetNewShowerType = useCallback(() => {
    setNewShowerType({
      name: '',
      manufacturer: '',
      model: '',
      operationType: '',
      finishWarranty: '',
      glassWarranty: '',
      hardwareWarranty: '',
      installationWarranty: '',
      markerColor: DEFAULT_MARKER_COLOR
    });
    setPendingShowerTypePicture(null);
    setShowerTypePictureUrl(null);
  }, []);
  
  const resetShowerTypeDialog = useCallback(() => {
    setShowShowerTypeDialog(false);
    setEditingShowerType(null);
    resetNewShowerType();
  }, [resetNewShowerType]);
  
  // ============================================
  // OPEN DIALOG FOR EDIT
  // ============================================
  const openEditShowerTypeDialog = useCallback((showerType) => {
    setEditingShowerType(showerType);
    setNewShowerType({
      name: showerType.name || '',
      manufacturer: showerType.manufacturer || '',
      model: showerType.model || '',
      operationType: showerType.operationType || '',
      showerStyle: showerType.showerStyle || '',
      fgFinish: showerType.fgFinish || '',
      fgManufacturer: showerType.fgManufacturer || '',
      ucFinishType: showerType.ucFinishType || '',
      ucFinish: showerType.ucFinish || '',
      ucManufacturer: showerType.ucManufacturer || '',
      finishWarranty: showerType.finishWarranty || '',
      glassWarranty: showerType.glassWarranty || '',
      hardwareWarranty: showerType.hardwareWarranty || '',
      installationWarranty: showerType.installationWarranty || '',
      markerColor: showerType.markerColor || DEFAULT_MARKER_COLOR
    });
    setShowerTypePictureUrl(
      showerType.pictureCloudinary?.urls?.medium || 
      showerType.pictureUrl || 
      null
    );
    setShowShowerTypeDialog(true);
  }, []);
  
  // ============================================
  // CONFIRM DELETE
  // ============================================
  const confirmDeleteShowerType = useCallback((showerTypeId) => {
    setDeleteShowerTypeId(showerTypeId);
    setShowDeleteShowerTypeModal(true);
  }, []);
  
  const cancelDeleteShowerType = useCallback(() => {
    setDeleteShowerTypeId(null);
    setShowDeleteShowerTypeModal(false);
  }, []);
  
  // ============================================
  // SELECTION HANDLERS
  // ============================================
  const toggleShowerTypeSelection = useCallback((id, event) => {
    setSelectedShowerTypeIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);
  
  const selectAllShowerTypes = useCallback((showerTypes) => {
    if (selectedShowerTypeIds.length === showerTypes.length) {
      setSelectedShowerTypeIds([]);
    } else {
      setSelectedShowerTypeIds(showerTypes.map(st => st.id));
    }
  }, [selectedShowerTypeIds]);
  
  const clearShowerTypeSelections = useCallback(() => {
    setSelectedShowerTypeIds([]);
  }, []);
  
  const resetNewShowerGlassType = useCallback(() => {
    setNewShowerGlassType({ name: '', manufacturer: '', glassBuild: '', glassThickness: '', features: [], color: '', warrantyYears: '' });
  }, []);

  return {
    // Operation loading
    showerOperationLoading,
    setShowerOperationLoading,
    
    // Selection states
    selectedShowerTypeIds,
    setSelectedShowerTypeIds,
    
    // Dialog states
    showShowerTypeDialog,
    setShowShowerTypeDialog,
    editingShowerType,
    setEditingShowerType,
    newShowerType,
    setNewShowerType,
    
    // Picture states
    pendingShowerTypePicture,
    setPendingShowerTypePicture,
    showerTypePictureUploading,
    setShowerTypePictureUploading,
    showerTypePictureUrl,
    setShowerTypePictureUrl,
    
    // Delete confirmation
    showDeleteShowerTypeModal,
    setShowDeleteShowerTypeModal,
    deleteShowerTypeId,
    setDeleteShowerTypeId,
    
    // Functions
    resetNewShowerType,
    resetShowerTypeDialog,
    openEditShowerTypeDialog,
    confirmDeleteShowerType,
    cancelDeleteShowerType,
    toggleShowerTypeSelection,
    selectAllShowerTypes,
    clearShowerTypeSelections,
    
    // Shower Glass Type states
    showShowerGlassTypeDialog, setShowShowerGlassTypeDialog,
    editingShowerGlassType, setEditingShowerGlassType,
    selectedShowerGlassTypeIds, setSelectedShowerGlassTypeIds,
    expandedShowerGlassTypeIds, setExpandedShowerGlassTypeIds,
    newShowerGlassType, setNewShowerGlassType,
    showDeleteShowerGlassTypeModal, setShowDeleteShowerGlassTypeModal,
    deleteShowerGlassTypeId, setDeleteShowerGlassTypeId,
    resetNewShowerGlassType,

    // Shower Hardware Set states
    showShowerHardwareSetDialog, setShowShowerHardwareSetDialog,
    editingShowerHardwareSet, setEditingShowerHardwareSet,
    selectedShowerHardwareSetIds, setSelectedShowerHardwareSetIds,
    newShowerHardwareSet, setNewShowerHardwareSet,
    newShowerHardwareItem, setNewShowerHardwareItem,
    showerHardwareSetStep, setShowerHardwareSetStep,
    editingShowerHardwareItemIndex, setEditingShowerHardwareItemIndex,
    showDeleteShowerHardwareSetModal, setShowDeleteShowerHardwareSetModal,
    deleteShowerHardwareSetId, setDeleteShowerHardwareSetId,

    // Shower Schedule states
    showShowerScheduleDialog, setShowShowerScheduleDialog,
    editingShowerSchedule, setEditingShowerSchedule,
    selectedShowerScheduleIds, setSelectedShowerScheduleIds,
    newShowerSchedule, setNewShowerSchedule,
    showShowerDetailDialog, setShowShowerDetailDialog,
    selectedShowerDetail, setSelectedShowerDetail,

    // Shower Schedule Filter state
    showerScheduleSearch, setShowerScheduleSearch,
    showerScheduleFilters, setShowerScheduleFilters,
    showerScheduleSortBy, setShowerScheduleSortBy,

    // Shower Floor Plan states
    selectedShowerElevation, setSelectedShowerElevation,
    selectedShowerElevationIds, setSelectedShowerElevationIds,
    showerBulkMarkerMode, setShowerBulkMarkerMode,
    showerBulkMarkerSchedule, setShowerBulkMarkerSchedule,
    pendingShowerBulkMarkers, setPendingShowerBulkMarkers,
    savingShowerBulkMarkers, setSavingShowerBulkMarkers
  };
};

export default useShowerState;
